import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Well, Drawer, EmptyState } from '@pushly/aqe/lib/components'
import { EditOutlined, MessageOutlined } from '@ant-design/icons'
import { Alert, Flex, Input, Skeleton, Spin, Splitter, Tag, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import * as randomstring from 'randomstring'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { AppService, AiGenerationDirectiveService, UserService } from '../../../services'
import { DomainDto } from '../../../dtos/domain'
import { getClassNames } from '../../../_utils/classnames'
import { simpleNotification } from '../../../_utils/utils'
import { VERTICAL_WELL_GAP } from '../../../theme/constants'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { BrandVoiceSummary } from './brand-voice-summary'
import { AiCompilingIndicator, BrandVoiceDecomposition, CompileState } from './brand-voice-decomposition'
import { applyReplacement, dimensionFor, groupSuggestionsByItem } from './promotion-suggestions'
import { locateSpan } from './span-locator'
import { PromotionModal, type IPromotionTarget } from './promotion-modal'
import { applyPromotionToCompiled, enforcedLimitsByAttribute } from './promotion-optimistic'
import { BRAND_VOICE_DOCS_URL } from './brand-voice-help-content'
import {
    BRAND_VOICE_DISPLAY_TIER_VISUAL,
    DISPLAY_TIER_ORDER,
    tierCounts,
    type BrandVoiceDisplayTier,
} from './brand-voice-tier-visual'
import { BrandVoiceTestWell } from './brand-voice-test'

dayjs.extend(relativeTime)

const PLACEHOLDER =
    'e.g. Write like a trusted editor. Never use exclamation points. Keep titles under 60 characters. Favor curiosity over hype.'

/**
 * Grafts a reviewed-but-not-yet-persisted decomposition onto a freshly saved directive.
 *
 * `create`/`update` only ever return `compiled_rules: null` — neither request struct carries a
 * decomposition, so the server has nothing to echo back. When `compileAndPersist` afterward
 * can't confirm that decomposition server-side (its own fresh recompile disagreed with what was
 * reviewed), handing the caller `res.data` as-is would regress the well from the compiled
 * layout the author just approved down to plain, unhighlighted text — worse than what they saw
 * before saving, over a persistence detail they didn't cause and can't see from the drawer.
 *
 * `compiled` is safe to graft on here specifically because it describes the exact body that was
 * just saved: `handleSubmit` only reaches this path once `disableSubmit` has already confirmed
 * the two match. It is never written anywhere — only ever displayed — so the fact that the
 * server has not (yet) confirmed it cannot leak into anything persisted. Reopening the drawer
 * re-fetches and re-checks it for real.
 */
export function withReviewedCompiledRules(
    saved: AiGenerationDirective,
    compiled: CompiledDirective,
): AiGenerationDirective {
    if (!saved.active_version) return saved
    return saved.clone({ active_version: saved.active_version.clone({ compiled_rules: compiled }) })
}

interface IBrandVoiceEditor {
    domainId: number
    directive: AiGenerationDirective | null
    visible: boolean
    onClose: (e: any) => void
    onSaved: (directive: AiGenerationDirective) => void
}

const BrandVoiceEditor: FC<IBrandVoiceEditor> = (props) => {
    const appSvc = useService(AppService)
    const directiveSvc = useService(AiGenerationDirectiveService)

    // Textareas normalize \r\n/\r to \n internally for both their rendered value and
    // selectionStart/layout metrics — but the raw server body can still contain \r\n. If `body`
    // state kept those \r characters, every span offset computed against it would drift right
    // by one character per line break that precedes the match (the browser already dropped
    // that \r when laying the text out), compounding across paragraphs. Normalizing at the one
    // point raw server text enters state keeps our offsets and the browser's in agreement.
    const initialBody = (props.directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const initialCompiled = props.directive?.active_version?.compiled_rules ?? null

    const [body, setBody] = useState<string>(initialBody)
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [compileState, setCompileState] = useState<CompileState>(initialCompiled ? 'done' : 'idle')
    const [compiled, setCompiled] = useState<CompiledDirective | null>(initialCompiled)
    // Promotion suggestions for whatever `compiled` currently holds. Purely additive
    // to the editor: they are fetched after a compile lands, and a failure leaves them empty
    // rather than surfacing an error — losing a hint must never look like losing the analysis.
    const [suggestions, setSuggestions] = useState<DirectivePromotionSuggestion[]>([])
    // The domain's own measured p90 per dimension, carried alongside the suggestions so the
    // confirm step can retarget one onto another field or unit without another round trip.
    const [contentStats, setContentStats] = useState<{
        p90ByAttribute: Record<string, number>
        p50ByAttribute: Record<string, number>
        sampleSize: number
    }>({ p90ByAttribute: {}, p50ByAttribute: {}, sampleSize: 0 })
    // The statement whose promotion is being confirmed, or null. Null closes the modal.
    const [promotionTarget, setPromotionTarget] = useState<IPromotionTarget | null>(null)
    /**
     * A compile running over content that is ALREADY on screen. `compileState === 'compiling'`
     * is the other case — a first compile, with nothing to preserve — and that one replaces the
     * panel entirely.
     *
     *   'blocking'  the author clicked Analyze and is waiting for the answer. The rules stay
     *               visible under a loading overlay and the editor is locked, so the text cannot
     *               drift out from under a result that is about to describe it.
     *   'quiet'     WE started this, after a promotion. The author did not ask and may well keep
     *               working, so nothing is taken away — just a line saying a check is running.
     *
     * The distinction is who is waiting. Locking someone out of their own text is only
     * defensible when they asked for the thing they are being made to wait for.
     */
    const [busyMode, setBusyMode] = useState<'blocking' | 'quiet' | null>(null)

    // The body whatever `compiled` currently holds actually reflects (a persisted result on
    // load, or the last successful recompile) — lets the effect below tell "the author edited
    // the text" apart from "the component re-rendered with the same text", so opening the
    // drawer on a directive with a saved decomposition never fires a redundant compile call.
    const lastCompiledBodyRef = useRef<string | undefined>(initialCompiled ? initialBody : undefined)

    // The most recently ISSUED compile request's text — not the most recently RESOLVED one.
    // Debouncing only cancels a pending timer; it can't cancel a request already in flight, so
    // two compiles can legitimately overlap (e.g. the author keeps editing while a slow compile
    // of a long body is still running). Without this guard, whichever response happens to
    // resolve last wins regardless of which was issued last — a stale, shorter-body response
    // arriving after a newer one would silently overwrite it with rule text and highlight
    // offsets that no longer match what's on screen.
    const latestRequestedTextRef = useRef<string>('')

    // Read inside `runCompile` instead of closing over `compiled`, which would rebuild the
    // callback on every compile and re-arm the debounced background compile with a stale one.
    const compiledRef = useRef<CompiledDirective | null>(initialCompiled)
    useEffect(() => {
        compiledRef.current = compiled
    }, [compiled])

    const runCompile = useCallback(
        async (text: string, opts: { mode?: 'blocking' | 'quiet' } = {}) => {
            latestRequestedTextRef.current = text

            if (!text.trim()) {
                setCompiled(null)
                setCompileState('idle')
                setSuggestions([])
                lastCompiledBodyRef.current = undefined
                return
            }
            // With something already on screen, `compileState` stays 'done' so the decomposition
            // keeps rendering and the mode decides how the wait is presented. Submit is not
            // weakened by that: it also requires the text on screen to equal
            // `lastCompiledBodyRef`, which only a FINISHED compile sets.
            if (compiledRef.current) setBusyMode(opts.mode ?? 'blocking')
            else setCompileState('compiling')
            // Dropped up front, not on the response: they describe the OLD decomposition, and
            // leaving them on screen through a recompile would invite applying a rewrite whose
            // offsets belong to text that is already being replaced.
            setSuggestions([])
            setPromotionTarget(null)
            const res = await directiveSvc.compilePreview(props.domainId, text)

            // A newer request has since been issued (and will supersede this one on its own
            // resolution) — applying this now-stale result would only reintroduce the race.
            if (latestRequestedTextRef.current !== text) return

            setBusyMode(null)

            if (res.ok && res.data) {
                setCompiled(res.data)
                setCompileState('done')
                lastCompiledBodyRef.current = text

                // Deliberately NOT awaited into the compile's own state transitions: the
                // decomposition is the answer the author asked for and must paint as soon as it
                // lands. Suggestions are an extra that arrives a moment later if it arrives.
                const suggestionRes = await directiveSvc.suggestPromotions(props.domainId, res.data)
                if (latestRequestedTextRef.current !== text) return
                if (suggestionRes.ok && suggestionRes.data) {
                    setSuggestions(suggestionRes.data.suggestions ?? [])
                    setContentStats({
                        p90ByAttribute: suggestionRes.data.p90_by_attribute ?? {},
                        p50ByAttribute: suggestionRes.data.p50_by_attribute ?? {},
                        sampleSize: suggestionRes.data.sample_size ?? 0,
                    })
                }
            } else if (compiledRef.current) {
                // A failed compile must not replace content the author is reading with an error
                // panel — the previous rules are still true for the previous text.
                // `lastCompiledBodyRef` stays behind, so `isStale` lights the Analyze button and
                // the hint below the textarea already says the rules predate the current text.
                setCompileState('done')
            } else {
                setCompileState('error')
            }
        },
        [directiveSvc, props.domainId],
    )

    // Grouped by the statement each is about — an ambiguous-subject statement produces one
    // suggestion per plausible field, and they are alternatives the author chooses between
    // rather than a list of edits to make.
    const suggestionsByItem = useMemo(() => groupSuggestionsByItem(suggestions), [suggestions])

    // Limits the author has already written, by binding attribute — so the confirm step can
    // refuse to aim a promotion at a dimension their own text already sets.
    const enforcedByAttribute = useMemo(() => enforcedLimitsByAttribute(compiled), [compiled])

    /**
     * Rewrites the author's own text, which is the ONLY way a promotion can take effect:
     * compiled rules are re-derived from the body on every compile, so a binding introduced any
     * other way would evaporate on the next Analyze.
     *
     * Three things happen together, and the order matters to how it feels:
     *   1. the sentence is spliced into the text
     *   2. the decomposition is updated LOCALLY so the rule appears under Guaranteed at once
     *   3. the real compile is started in the background, and replaces (2) when it lands
     *
     * (2) is a prediction, not a result — see `applyPromotionToCompiled` for why it can be an
     * accurate one. It exists because the alternative was asking the author to click Analyze and
     * watch a spinner to see the outcome of a change they had already confirmed.
     *
     * (3) is NOT an extra compile. `compileAndPersist` reuses the same Redis pre-warm keyed on
     * body text, so this is precisely the compile that Save would otherwise have run — started
     * earlier, while the author is still reading, instead of at the moment they press Save.
     */
    const handleConfirmPromotion = (
        replaces: DirectivePromotionSuggestion['replaces'],
        sentence: string,
        dimension: { field: 'title' | 'body'; unit: 'characters' | 'words'; value: number },
    ) => {
        // Offsets were computed against the text Analyze compiled, which is `body.trim()` — the
        // editor's own state keeps the author's surrounding whitespace, so the compiled body is
        // handed over to re-base them rather than left to a quote search to paper over.
        const located = locateSpan(body, replaces, lastCompiledBodyRef.current)
        const next = located && applyReplacement(body, replaces, sentence, lastCompiledBodyRef.current)
        if (!located || next == null) {
            // The modal disables its confirm when the span can't be located, so this is only
            // reachable if the text moved between opening it and confirming.
            simpleNotification(
                'warning',
                'This text has changed since you checked it — click “Generate rules” and try again.',
            )
            setPromotionTarget(null)
            return
        }

        const itemText = promotionTarget?.suggestions[0].item_text
        setBody(next)
        setIsDirty(true)
        setPromotionTarget(null)

        // Where the new sentence now sits — known exactly, since we just placed it. The
        // Guaranteed row it produces therefore links back to its own source text immediately,
        // rather than waiting for the compiler to re-locate a quote it wrote itself.
        const sentenceSpan = { start: located.start, end: located.start + sentence.length, quote: sentence }
        if (compiled && itemText) {
            setCompiled(
                applyPromotionToCompiled(
                    compiled,
                    itemText,
                    dimensionFor(dimension.field, dimension.unit),
                    dimension.value,
                    sentenceSpan,
                ),
            )
        }

        // Every remaining suggestion's offsets were computed against the body as it was BEFORE
        // this splice, so they have all just shifted. `locateSpan` re-finds them by quote,
        // but only the ones for OTHER statements — the alternatives that shared this statement
        // are now moot, since the sentence they were about no longer exists.
        setSuggestions((current) => current.filter((s) => s.item_text !== itemText))

        scheduleBackgroundCompile(next.trim())
    }

    /**
     * Starts the real compile behind the local prediction, coalescing a run of promotions into
     * one request.
     *
     * Promoting three suggestions in a row is a normal thing to do, and firing a ten-second
     * compile after each would burn two of them on text that was superseded before they
     * returned. The delay is short enough to be invisible when only one promotion happens.
     */
    const backgroundCompileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scheduleBackgroundCompile = (text: string) => {
        if (backgroundCompileTimerRef.current) clearTimeout(backgroundCompileTimerRef.current)
        backgroundCompileTimerRef.current = setTimeout(() => runCompile(text, { mode: 'quiet' }), 1200)
    }

    useEffect(() => {
        return () => {
            if (backgroundCompileTimerRef.current) clearTimeout(backgroundCompileTimerRef.current)
        }
    }, [])

    // Explicit "Analyze" trigger, not a debounced auto-compile: firing a compile
    // after every pause in typing meant any hesitation mid-sentence kicked off a request
    // against still-incomplete text — annoying in itself, and the source of most of the
    // "stale result overwritten by a slower one" races `latestRequestedTextRef` above exists
    // to guard against. The author decides when the text is actually ready to analyze.
    // `isStale` is what the Analyze button and decomposition below key off of to signal that
    // the rules on screen no longer reflect what's currently written.
    // Compared against `body.trim()`, not raw `body`: `runCompile` is always called with
    // trimmed text below (see its call sites), matching exactly what `handleSubmit` persists
    // as the directive's body. Comparing untrimmed `body` here would flag surrounding
    // whitespace alone as "changed since last analyzed", and — more importantly — letting
    // Analyze and Submit ever compile two DIFFERENT strings for the same author-visible text
    // is exactly what broke `compileAndPersist`'s equality check server-side: its `sources`
    // spans are absolute character offsets into whatever exact string was compiled, so even a
    // stray trailing newline shifts every offset and makes an otherwise-identical recompile
    // fail to match.
    const isStale = body.trim() !== '' && body.trim() !== lastCompiledBodyRef.current

    const handleSubmit = async () => {
        const trimmed = body.trim()
        if (!trimmed) {
            simpleNotification('error', 'Add some text before saving your Brand Voice.')
            return
        }
        if (!compiled) {
            // `disableSubmit` below should prevent reaching here without a completed, current
            // compile — defensive guard, not the primary gate.
            simpleNotification('error', 'Still checking your rules — try saving again in a moment.')
            return
        }

        setSaving(true)
        try {
            const res = props.directive
                ? await directiveSvc.update(
                      props.domainId,
                      props.directive.id,
                      UpdateAiGenerationDirectiveRequest.from({ body: trimmed }),
                  )
                : await directiveSvc.create(
                      props.domainId,
                      CreateAiGenerationDirectiveRequest.from({
                          role: 'brand_voice',
                          name: 'Brand Voice',
                          body: trimmed,
                      }),
                  )

            if (!res.ok || !res.data) return

            // Persist the EXACT decomposition just displayed and approved (`compiled`) — the
            // server independently recompiles and only writes it if its own fresh result
            // matches, so the next time this directive opens, its saved rules are there to
            // show immediately. Reuses the Redis pre-warm from the Analyze click above (same
            // exact text, since Submit is disabled until that Analyze result is current), so
            // this is typically instant.
            const compiledRes = await directiveSvc.compileAndPersist(props.domainId, res.data.id, compiled)

            if (compiledRes.ok && compiledRes.data) {
                simpleNotification('success', 'Your Brand Voice has been saved.')
                props.onSaved(compiledRes.data)
            } else {
                // The server's fresh recompile no longer matched what was reviewed (an expired
                // preview cache, a compiler change, or a stale review) — the text itself saved,
                // but the reviewed decomposition was NOT persisted rather than silently
                // persisting something different from what was approved. See
                // `withReviewedCompiledRules` for why the well still shows it regardless.
                simpleNotification(
                    'warning',
                    'Your text was saved, but the rules changed since you last checked them. Reopen your Brand Voice to review them before they start applying.',
                )
                props.onSaved(withReviewedCompiledRules(res.data, compiled))
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-brand-voice-editor')}
            title="Edit your Brand Voice"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            // Never enabled mid-compile or against stale/errored text — Submit only becomes
            // available once `compiled` reflects a successfully-finished compile of the exact
            // text on screen, so what gets persisted is guaranteed to be what was reviewed.
            //
            // Deliberately NOT gated on `saving`: aqe's SubmitButton renders a structurally
            // different element (a bare span vs. a Tooltip-wrapped one) depending on whether
            // it's disabled, so flipping `disableSubmit` true as a synchronous side effect of
            // the click (which `setSaving(true)` inside handleSubmit is) remounts the inner
            // Button mid-click and discards the loading state it had just set — the spinner
            // never gets a chance to paint. Re-entrancy during the save is already handled by
            // aqe's own Button (it no-ops a click while its internal `isLoading` is true), so
            // this doesn't need its own guard against a double-submit.
            disableSubmit={!isDirty || compileState !== 'done' || body.trim() !== lastCompiledBodyRef.current}
            onClose={props.onClose}
            open={props.visible}
            size={1280}
        >
            <Flex vertical gap={16} className={getClassNames('brand-voice-editor-body')}>
                <Typography.Text className={getClassNames('brand-voice-intro')}>
                    Describe your writing style in your own words. Anything we can check automatically, we enforce on
                    every piece of AI-written content. Everything else guides the tone. It never touches content you
                    write yourself.{' '}
                    <Typography.Link href={BRAND_VOICE_DOCS_URL}>Learn how rules are generated.</Typography.Link>
                </Typography.Text>

                {/* A direct viewport-relative height, not a percentage chained up through
                    Flex -> the antd Drawer body -> the Drawer's own panel sizing — that chain
                    depends on several nested elements each correctly resolving a definite
                    height for a `height: 100%` below them to mean anything, and in practice it
                    collapsed to the textarea's tiny intrinsic default instead of filling the
                    drawer. A fixed calc against the viewport sidesteps all of that: 240px
                    accounts for the drawer header, its body padding, and the intro paragraph
                    above this row: same on-screen effect, without depending on ancestors we
                    don't control resolving heights the way we'd expect. `Splitter.Panel`
                    children stretch to fill this explicit height automatically (antd's own
                    cross-axis stretch, not something we have to wire up). */}
                {/* `resizable={false}` on both panels drops the drag handle — the center divider
                    it draws between them — while keeping the fixed 1/3-2/3 split from each
                    panel's own `style`. Still a `Splitter`, not a plain two-column `Flex`, because
                    its panels already stretch to the explicit height set above; switching
                    components would mean re-deriving that. */}
                <Splitter style={{ height: 'calc(100vh - 202px)', minHeight: 320 }}>
                    <Splitter.Panel resizable={false} style={{ minWidth: '33%' }}>
                        <div
                            className={getClassNames('brand-voice-text-panel')}
                            style={{ margin: '0 12px', height: '100%', minHeight: 0 }}
                        >
                            <Well
                                variant="default"
                                padding="small"
                                titleSize="sm"
                                shadow={true}
                                style={{ height: 'calc(100% - 14px)', display: 'flex', flexDirection: 'column' }}
                                bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 6 }}
                                title={
                                    <Flex align="center" gap={6}>
                                        <MessageOutlined />
                                        <Typography.Text strong>Brand Voice</Typography.Text>
                                    </Flex>
                                }
                                actions={
                                    <Button
                                        size="small"
                                        type={isStale ? 'primary' : 'default'}
                                        loading={compileState === 'compiling' || !!busyMode}
                                        // Also blocked while saving — a click here mid-save would
                                        // kick off a recompile against text the save is actively
                                        // persisting, whose result could land after the save
                                        // finishes and make `compiled` disagree with what was just
                                        // written.
                                        disabled={compileState === 'compiling' || !!busyMode || !isStale || saving}
                                        // Trimmed to match exactly what handleSubmit persists as
                                        // the directive's body — see `isStale`'s docstring above.
                                        //
                                        // Re-analyzing runs in the BACKGROUND whenever there is
                                        // already a decomposition on screen: replacing a full panel of
                                        // rules with a spinner for ten seconds throws away everything
                                        // the author was reading, and the previous result stays true
                                        // for the previous text the whole time it is running. Only a
                                        // first-ever compile has nothing to keep.
                                        onClick={() => runCompile(body.trim())}
                                    >
                                        Generate rules
                                    </Button>
                                }
                            >
                                <Input.TextArea
                                    // Locked while the author waits on a compile THEY asked for —
                                    // editing mid-compile means the result lands describing text that
                                    // no longer exists. Covers the first compile too, which has no
                                    // previous decomposition to keep and so never sets `busyMode`.
                                    // A quiet post-promotion refresh never locks: nobody asked for it
                                    // and they may still be working.
                                    disabled={compileState === 'compiling' || busyMode === 'blocking'}
                                    value={body}
                                    onChange={(e) => {
                                        // Defensive: the DOM's textarea sanitization already normalizes
                                        // typed/pasted input to \n, but keep state consistent regardless
                                        // of how a future caller might set this value.
                                        setBody(e.target.value.replace(/\r\n?/g, '\n'))
                                        setIsDirty(true)
                                    }}
                                    placeholder={PLACEHOLDER}
                                    style={{ flex: 1, resize: 'none' }}
                                    styles={{ textarea: { height: '100%' } }}
                                />
                                {isStale && (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        You’ve changed the text — click “Generate rules” to update the rules on the
                                        right.
                                    </Typography.Text>
                                )}
                            </Well>
                        </div>
                    </Splitter.Panel>
                    <Splitter.Panel resizable={false} style={{ flex: 2, minWidth: '45%' }}>
                        <div
                            style={{
                                margin: '0 12px',
                                height: '100%',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* The overlay wraps the SCROLL CONTAINER, not the rules inside it.
                                Wrapping the content meant antd centred the indicator on the
                                content's full scroll height, so on a long decomposition it sat
                                far below the fold — a loading state you have to scroll to find.
                                Anchored here, the box is the visible area and centring means what
                                it looks like it means. */}
                            <Spin
                                spinning={busyMode === 'blocking'}
                                wrapperClassName={getClassNames('bvd-overlay')}
                                // antd dims the wrapped content and sets `pointer-events: none` on
                                // it — exactly the "visible but not actionable" state wanted, from
                                // the design system rather than a hand-rolled scrim.
                                indicator={<AiCompilingIndicator active={busyMode === 'blocking'} />}
                            >
                                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                    <BrandVoiceDecomposition
                                        state={compileState}
                                        compiled={compiled}
                                        refreshing={busyMode === 'quiet'}
                                        onRetry={() => runCompile(body.trim())}
                                        suggestionsByItem={suggestionsByItem}
                                        onPromote={(group) =>
                                            setPromotionTarget({
                                                suggestions: group,
                                                p90ByAttribute: contentStats.p90ByAttribute,
                                                p50ByAttribute: contentStats.p50ByAttribute,
                                                sampleSize: contentStats.sampleSize,
                                                enforcedByAttribute,
                                            })
                                        }
                                    />
                                </div>
                            </Spin>
                        </div>
                    </Splitter.Panel>
                </Splitter>
            </Flex>

            <PromotionModal
                target={promotionTarget}
                body={body}
                compiledBody={lastCompiledBodyRef.current}
                onCancel={() => setPromotionTarget(null)}
                onConfirm={handleConfirmPromotion}
            />
        </Drawer>
    )
}

interface IBrandVoiceWell {
    /** Not just an id: `BrandVoiceTestWell`'s cuttlefish preview needs the domain's display name
     * for the notification's app chrome. */
    domain: DomainDto
    canEdit: boolean
    /** Whether FEAT_PUSHLY_AI is on for this domain — purely for the "Live"/"Off" tag; this well
     * is only ever mounted once the flag is already confirmed on, so it never gates rendering. */
    enabled: boolean
}

export const BrandVoiceWell: FC<IBrandVoiceWell> = (props) => {
    const directiveSvc = useService(AiGenerationDirectiveService)
    const userSvc = useService(UserService)

    const [loading, setLoading] = useState(true)
    const [directive, setDirective] = useState<AiGenerationDirective | null>(null)
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')
    // Resolved separately from the directive fetch: `updated_by` on the wire is only a user id,
    // and there is no org-users store already loaded to look it up against — see `fetchUserById`.
    const [updatedByName, setUpdatedByName] = useState<string | null>(null)
    // null until the author picks a tag themselves — see `activeTier` below for the fallback
    // this defers to until then.
    const [selectedTier, setSelectedTier] = useState<BrandVoiceDisplayTier | null>(null)

    // `cancelled` is checked after the await: switching domains starts a second fetch while the
    // first is still open, and without this the slower response wins whichever domain it belongs
    // to — showing one domain's Brand Voice under another's heading.
    const loadBrandVoice = async (isCancelled: () => boolean = () => false) => {
        setLoading(true)
        const res = await directiveSvc.fetchBrandVoice(props.domain.id)
        if (isCancelled()) return
        if (res.ok) {
            setDirective(res.data ?? null)
        }
        setLoading(false)
    }

    useEffect(() => {
        let cancelled = false
        loadBrandVoice(() => cancelled)
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.domain.id])

    useEffect(() => {
        let cancelled = false
        const updatedBy = directive?.updated_by
        if (!updatedBy) {
            setUpdatedByName(null)
            return
        }
        userSvc.fetchUserById(updatedBy, { muteErrors: true }).then((user) => {
            if (!cancelled) setUpdatedByName(user?.name ?? null)
        })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directive?.updated_by])

    const body = (directive?.active_version?.body ?? '').replace(/\r\n?/g, '\n')
    const compiledRules = directive?.active_version?.compiled_rules ?? null
    const counts = useMemo(() => tierCounts(compiledRules), [compiledRules])
    const updatedAt = directive?.updated_at

    // The tag the author clicked, if any, else whichever tier actually has something in it —
    // a directive with no guarantees should not default to an empty Guaranteed pane.
    const activeTier: BrandVoiceDisplayTier =
        selectedTier ?? DISPLAY_TIER_ORDER.find((t) => counts[t] > 0) ?? 'guaranteed'

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('bvw')}>
            <Well
                title="Brand Voice"
                actions={<Tag color={props.enabled ? 'success' : undefined}>{props.enabled ? 'Live' : 'Off'}</Tag>}
                footerMode="hidden"
            >
                <Skeleton loading={loading} active={true} title={false}>
                    <Flex vertical gap={8}>
                        <Typography.Paragraph className="ai-muted-text" style={{ marginBottom: 0 }}>
                            Describe how you want AI-written content to sound. Rules we can check automatically are
                            enforced every time; everything else guides the tone.{' '}
                            <Typography.Link href={BRAND_VOICE_DOCS_URL}>Learn more</Typography.Link>
                        </Typography.Paragraph>

                        {updatedAt && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Edited {dayjs(updatedAt).fromNow()}
                                {updatedByName ? ` by ${updatedByName}` : ''}
                            </Typography.Text>
                        )}

                        <Flex gap={24} className={getClassNames('bvw-stats')}>
                            <Flex vertical gap={0}>
                                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                                    Guaranteed
                                </Typography.Text>
                                <Typography.Text strong>{counts.guaranteed}</Typography.Text>
                            </Flex>
                            <Flex vertical gap={0}>
                                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                                    Guidance
                                </Typography.Text>
                                <Typography.Text strong>{counts.guidance}</Typography.Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </Skeleton>
            </Well>

            <Well
                className="nested"
                title="Directive"
                actions={
                    <Flex align="center" gap={8}>
                        {!loading &&
                            body &&
                            DISPLAY_TIER_ORDER.map((tier) => {
                                const isActive = activeTier === tier
                                return (
                                    <Tag
                                        key={tier}
                                        variant="filled"
                                        bordered={isActive}
                                        onClick={() => setSelectedTier(tier)}
                                        style={{
                                            cursor: 'pointer',
                                            color: isActive ? BRAND_VOICE_DISPLAY_TIER_VISUAL[tier].color : undefined,
                                            borderColor: isActive
                                                ? BRAND_VOICE_DISPLAY_TIER_VISUAL[tier].color
                                                : undefined,
                                        }}
                                    >
                                        {counts[tier]} {tier === 'guaranteed' ? 'Guaranteed' : 'Guidance'}
                                    </Tag>
                                )
                            })}
                        <Button
                            size="small"
                            onClick={() => {
                                setDrawerKey(randomstring.generate())
                                setShowDrawer(true)
                            }}
                            disabled={!props.canEdit || loading}
                        >
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    </Flex>
                }
                footerMode="hidden"
                // A LOCKED height, not a min/max range — the well around `BrandVoiceSummary`'s
                // two columns must never scroll itself, only ever show them at a constant size.
                // `overflow: hidden` (not `auto`) is what actually guarantees that: with `auto`,
                // if the well's own body ever ends up taller than intended for any reason — a
                // flex quirk, a future content addition — the browser puts a second scrollbar on
                // THIS element instead of relying on the inner columns' own `overflow-y: auto`
                // (each capped at `PANE_COLLAPSED_HEIGHT` in brand-voice-summary.tsx), which is
                // what actually happened: this well scrolled as a whole instead of its columns
                // scrolling independently. `hidden` makes that structurally impossible — this
                // well cannot show a scrollbar of its own no matter what.
                bodyStyle={{ height: 340, overflow: 'hidden' }}
                shadow={true}
                showDividers
            >
                {!loading &&
                    (body ? (
                        <BrandVoiceSummary body={body} compiled={compiledRules} activeTier={activeTier} />
                    ) : (
                        <EmptyState
                            size="small"
                            align="start"
                            title="No Brand Voice set yet"
                            description="Until you do, AI-written content uses our default style."
                        />
                    ))}

                <BrandVoiceEditor
                    key={drawerKey}
                    domainId={props.domain.id}
                    directive={directive}
                    visible={showDrawer}
                    onClose={() => setShowDrawer(false)}
                    onSaved={(updated) => {
                        setDirective(updated)
                        setShowDrawer(false)
                    }}
                />
            </Well>

            <BrandVoiceTestWell domain={props.domain} compiled={compiledRules} />
        </Flex>
    )
}
